import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { LogOut, Download, Search, Trash2, Upload, FileText, Users, Filter, Edit3, Save, X, BarChart3 } from 'lucide-react';
import { VisitorsPanel } from '../components/VisitorsPanel';
import { useAuth, api, API, formatApiError } from '../auth/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import Logo from '../components/Logo';

const STATUSES = ['new', 'contacted', 'admitted', 'closed'];
const STATUS_LABELS = { new: 'New', contacted: 'Contacted', admitted: 'Admitted', closed: 'Closed' };
const STATUS_COLORS = {
  new: 'bg-amber-100 text-amber-800',
  contacted: 'bg-blue-100 text-blue-800',
  admitted: 'bg-green-100 text-green-800',
  closed: 'bg-stone-200 text-stone-700',
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#fff7f1]">
      <header className="bg-white border-b border-orange-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-stone-500 hidden sm:inline">Signed in as <strong>{user.username}</strong></span>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout-btn" className="border-orange-200 hover:bg-coral-light">
              <LogOut className="w-4 h-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <h1 className="font-serif text-3xl font-semibold">Admin Dashboard</h1>
        <p className="text-stone-500 mt-1">Manage admission leads and study resources.</p>

        <Tabs defaultValue="leads" className="mt-6">
          <TabsList className="bg-white border border-orange-100">
            <TabsTrigger value="leads" className="data-[state=active]:!bg-[#ee5a47] data-[state=active]:!text-white">
              <Users className="w-4 h-4 mr-1.5" /> Leads
            </TabsTrigger>
            <TabsTrigger value="papers" className="data-[state=active]:!bg-[#ee5a47] data-[state=active]:!text-white">
              <FileText className="w-4 h-4 mr-1.5" /> Sample Papers
            </TabsTrigger>
            <TabsTrigger value="visitors" data-testid="visitors-tab" className="data-[state=active]:!bg-[#ee5a47] data-[state=active]:!text-white">
              <BarChart3 className="w-4 h-4 mr-1.5" /> Visitors
            </TabsTrigger>
          </TabsList>
          <TabsContent value="leads" className="mt-5"><LeadsPanel /></TabsContent>
          <TabsContent value="papers" className="mt-5"><PapersPanel /></TabsContent>
          <TabsContent value="visitors" className="mt-5"><VisitorsPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function LeadStatCards({ leads }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
      {STATUSES.map((s) => (
        <div key={s} className="bg-white rounded-2xl border border-orange-100 p-4">
          <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{STATUS_LABELS[s]}</div>
          <div className="font-serif text-3xl font-semibold mt-1">{leads.filter((l) => l.status === s).length}</div>
        </div>
      ))}
    </div>
  );
}

function LeadsToolbar({ search, setSearch, statusF, setStatusF, onApply, onExport }) {
  return (
    <div className="bg-white rounded-2xl border border-orange-100 p-4 mb-4 flex flex-wrap gap-3 items-center">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input placeholder="Search name, phone, email..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onApply()}
          data-testid="leads-search-input"
          className="pl-9" />
      </div>
      <Select value={statusF} onValueChange={setStatusF}>
        <SelectTrigger className="w-[160px]" data-testid="leads-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button onClick={onApply} variant="outline" className="border-orange-200"><Filter className="w-4 h-4 mr-1.5" /> Apply</Button>
      <Button onClick={onExport} data-testid="leads-export-btn" className="btn-primary border-0"><Download className="w-4 h-4 mr-1.5" /> Export CSV</Button>
    </div>
  );
}

function LeadsTable({ leads, busy, onEdit, onDelete }) {
  return (
    <div className="bg-white rounded-2xl border border-orange-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="bg-coral-light text-stone-700">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Date</th>
              <th className="text-left px-4 py-3 font-semibold">Student</th>
              <th className="text-left px-4 py-3 font-semibold">Class</th>
              <th className="text-left px-4 py-3 font-semibold">Branch</th>
              <th className="text-left px-4 py-3 font-semibold">Phone</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && (
              <tr><td colSpan={7} className="text-center text-stone-500 py-12">{busy ? 'Loading...' : 'No leads yet.'}</td></tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-orange-50 hover:bg-orange-50/40">
                <td className="px-4 py-3 text-stone-600 whitespace-nowrap">{new Date(l.createdAt).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{l.studentName}</div>
                  {l.parentName && <div className="text-xs text-stone-500">Parent: {l.parentName}</div>}
                </td>
                <td className="px-4 py-3">{l.classApplyingFor}</td>
                <td className="px-4 py-3">{l.branch}</td>
                <td className="px-4 py-3">
                  <a href={`tel:${l.phone}`} className="text-coral hover:underline">{l.phone}</a>
                  {l.email && <div className="text-xs text-stone-500">{l.email}</div>}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[l.status]}`}>{STATUS_LABELS[l.status]}</span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => onEdit(l)} className="text-coral hover:bg-coral-light rounded p-1.5"><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => onDelete(l.id)} className="text-red-600 hover:bg-red-50 rounded p-1.5 ml-1"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LeadsPanel() {
  const { toast } = useToast();
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState('all');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (statusF !== 'all') params.status = statusF;
      if (search) params.search = search;
      const r = await api.get('/leads', { params });
      setLeads(r.data);
    } catch (e) {
      toast({ title: 'Failed to load leads' });
    } finally { setBusy(false); }
  }, [statusF, search, toast]);

  useEffect(() => {
    load();
    // search is applied via Enter / Apply button, not per keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusF]);

  const onDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await api.delete(`/leads/${id}`);
    setLeads((l) => l.filter((x) => x.id !== id));
    toast({ title: 'Lead deleted' });
  };

  const exportCSV = async () => {
    const res = await fetch(`${API}/leads/export.csv`, { credentials: 'include' });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `rn_leads_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <LeadStatCards leads={leads} />
      <LeadsToolbar search={search} setSearch={setSearch} statusF={statusF} setStatusF={setStatusF} onApply={load} onExport={exportCSV} />
      <LeadsTable leads={leads} busy={busy} onEdit={setEditing} onDelete={onDelete} />
      {editing && <EditLeadModal lead={editing} onClose={() => setEditing(null)} onSaved={(u) => { setLeads((arr) => arr.map((x) => x.id === u.id ? u : x)); setEditing(null); }} />}
    </div>
  );
}

function EditLeadModal({ lead, onClose, onSaved }) {
  const { toast } = useToast();
  const [status, setStatus] = useState(lead.status);
  const [notes, setNotes] = useState(lead.notes || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/leads/${lead.id}`, { status, notes });
      onSaved(r.data);
      toast({ title: 'Lead updated' });
    } catch (e) { toast({ title: 'Update failed' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6" data-testid="edit-lead-modal">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-serif text-2xl font-semibold">{lead.studentName}</h3>
            <p className="text-stone-500 text-sm mt-1">{lead.classApplyingFor} · {lead.branch} · {lead.phone}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-stone-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="mt-5 space-y-4">
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="edit-lead-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Call notes, follow-up plans..." />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="edit-lead-save" className="btn-primary border-0"><Save className="w-4 h-4 mr-1.5" /> Save</Button>
        </div>
      </div>
    </div>
  );
}

function PapersPanel() {
  const { toast } = useToast();
  const [papers, setPapers] = useState([]);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    api.get('/resources').then((r) => setPapers(r.data)).catch(() => toast({ title: 'Failed to load papers' }));
    // load once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm('Delete this paper?')) return;
    await api.delete(`/resources/${id}`);
    setPapers((p) => p.filter((x) => x.id !== id));
    toast({ title: 'Deleted' });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-stone-600">{papers.length} papers</div>
        <Button onClick={() => setShowUpload(true)} data-testid="upload-paper-btn" className="btn-primary border-0"><Upload className="w-4 h-4 mr-1.5" /> Upload Paper</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {papers.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-orange-100 p-5">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-lg bg-coral-light text-coral flex items-center justify-center"><FileText className="w-5 h-5" /></div>
              <span className="text-xs text-stone-500">{(p.sizeBytes/1024/1024).toFixed(2)} MB</span>
            </div>
            <h3 className="font-serif font-semibold text-base mt-3">{p.title}</h3>
            <div className="flex flex-wrap gap-1.5 mt-2 text-xs">
              <span className="px-2 py-0.5 rounded-full bg-coral-light text-coral font-medium">{p.board}</span>
              <span className="px-2 py-0.5 rounded-full bg-stone-100">{p.classLevel}</span>
              <span className="px-2 py-0.5 rounded-full bg-stone-100">{p.year}</span>
            </div>
            <div className="mt-4 pt-3 border-t border-orange-50 flex justify-between items-center">
              <a href={`${API}/resources/${p.id}/download`} target="_blank" rel="noreferrer" className="text-coral text-sm font-medium hover:underline inline-flex items-center gap-1"><Download className="w-3.5 h-3.5"/> Preview</a>
              <button onClick={() => onDelete(p.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded"><Trash2 className="w-4 h-4"/></button>
            </div>
          </div>
        ))}
        {papers.length === 0 && <div className="col-span-full text-center text-stone-500 py-12">No papers uploaded yet.</div>}
      </div>

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUploaded={(p) => { setPapers((arr) => [p, ...arr]); setShowUpload(false); }} />}
    </div>
  );
}

function UploadModal({ onClose, onUploaded }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title:'', board:'CBSE', subject:'', type:'Sample Paper', classLevel:'Class 10', year: new Date().getFullYear() });
  const [file, setFile] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast({ title: 'Please select a PDF file' }); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('file', file);
      const r = await api.post('/resources', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(r.data);
      toast({ title: 'Paper uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: formatApiError(e?.response?.data?.detail, 'Try again') });
    } finally { setBusy(false); }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-lg p-6" data-testid="upload-paper-modal">
        <div className="flex justify-between items-start">
          <h3 className="font-serif text-2xl font-semibold">Upload Sample Paper</h3>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-stone-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => update('title', e.target.value)} required placeholder="ICSE Class 10 - Maths Sample Paper" />
          </div>
          <div className="space-y-1.5">
            <Label>Board</Label>
            <Select value={form.board} onValueChange={(v) => update('board', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ICSE">ICSE</SelectItem>
                <SelectItem value="CBSE">CBSE</SelectItem>
                <SelectItem value="ISC">ISC</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => update('type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Sample Paper">Sample Paper</SelectItem>
                <SelectItem value="Question Paper">Question Paper</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Class</Label>
            <Select value={form.classLevel} onValueChange={(v) => update('classLevel', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Class 5','Class 6','Class 7','Class 8','Class 9','Class 10','Class 11','Class 12'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input value={form.subject} onChange={(e) => update('subject', e.target.value)} required placeholder="Mathematics" />
          </div>
          <div className="space-y-1.5">
            <Label>Year</Label>
            <Input type="number" value={form.year} onChange={(e) => update('year', parseInt(e.target.value)||0)} required />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>PDF File</Label>
            <Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} required />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="upload-paper-submit" className="btn-primary border-0"><Upload className="w-4 h-4 mr-1.5" /> {busy ? 'Uploading...' : 'Upload'}</Button>
        </div>
      </form>
    </div>
  );
}
